import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/shared/api';
import { Lock, ShieldAlert, LockOpen, Upload, Loader2, FileText, X, Sparkles } from 'lucide-react';

interface Document {
  id: string;
  bap_number: string | null;
  case_id: string | null;
  document_type?: string;
  satker: string;
  classification: string;
  status: string;
  page_count: number | null;
  uploaded_at: string;
}

const DOC_TYPE_LABEL: Record<string, string> = {
  BAP_SAKSI: 'BAP Saksi',
  BAP_TERSANGKA: 'BAP Tersangka',
  BAP_KORBAN: 'BAP Korban',
  BAP_AHLI: 'BAP Ahli',
  BAP_TKP: 'BAP TKP',
  BAP_PENANGKAPAN: 'BAP Penangkapan',
  BAP_PENGGELEDAHAN: 'BAP Penggeledahan',
  BAP_PENYITAAN: 'BAP Penyitaan',
  LAINNYA: 'Lainnya',
};

const KLAS_STYLE: Record<string, { color: string; bg: string; border: string; Icon: any; label: string }> = {
  BIASA: { color: 'text-ink-2', bg: '', border: 'border-ink-2', Icon: LockOpen, label: 'Biasa' },
  TERBATAS: { color: 'text-bluestamp', bg: '', border: 'border-bluestamp', Icon: Lock, label: 'Terbatas' },
  RAHASIA: { color: 'text-maroon', bg: '', border: 'border-maroon', Icon: ShieldAlert, label: 'Rahasia' },
  SANGAT_RAHASIA: { color: 'text-crimson', bg: 'bg-crimson/5', border: 'border-crimson', Icon: ShieldAlert, label: 'Sangat Rahasia' },
};

export default function BerkasPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') || '';
  const klasFilter = searchParams.get('classification') || '';
  const typeFilter = searchParams.get('document_type') || '';

  const { data, isLoading } = useQuery<Document[]>({
    queryKey: ['documents'],
    queryFn: () => api('/api/v1/documents'),
    refetchInterval: 5000,
  });

  const filtered = (data || []).filter((d) => {
    if (statusFilter && d.status !== statusFilter) return false;
    if (klasFilter) {
      if (klasFilter === 'RAHASIA' && !['RAHASIA', 'SANGAT_RAHASIA'].includes(d.classification)) return false;
      else if (klasFilter !== 'RAHASIA' && d.classification !== klasFilter) return false;
    }
    if (typeFilter && d.document_type !== typeFilter) return false;
    return true;
  });

  const setTypeFilter = (t: string) => {
    const p: Record<string, string> = {};
    if (statusFilter) p.status = statusFilter;
    if (klasFilter) p.classification = klasFilter;
    if (t) p.document_type = t;
    setSearchParams(p, { replace: true });
  };

  const clearFilters = () => setSearchParams({}, { replace: true });
  const activeFilters: { label: string; value: string }[] = [];
  if (statusFilter) activeFilters.push({ label: 'Status', value: statusFilter });
  if (klasFilter) activeFilters.push({ label: 'Klasifikasi', value: klasFilter });
  if (typeFilter) activeFilters.push({ label: 'Jenis', value: DOC_TYPE_LABEL[typeFilter] || typeFilter });

  return (
    <div>
      <div className="mb-4 pb-2 border-b-2 border-ink flex items-baseline gap-3 flex-wrap">
        <FileText className="w-5 h-5 text-brass self-center" />
        <h1 className="font-display font-bold text-[22px] leading-tight tracking-tight">Berkas</h1>
        <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3">— Daftar Berkas Perkara</span>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-ink-3">
          {(data || []).length} BAP terdaftar
        </span>
      </div>

      {/* TYPE FILTER STRIP */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3">Jenis Dokumen:</span>
        <button
          onClick={() => setTypeFilter('')}
          className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider border ${
            !typeFilter ? 'bg-ink text-paper border-ink' : 'border-ink-line text-ink-2 hover:border-ink'
          }`}
        >
          Semua
        </button>
        {Object.entries(DOC_TYPE_LABEL).map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTypeFilter(t)}
            className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider border ${
              typeFilter === t ? 'bg-brass text-paper border-brass' : 'border-ink-line text-ink-2 hover:border-brass hover:text-brass'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ACTIVE FILTER CHIPS */}
      {activeFilters.length > 0 && (
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3">Filter aktif:</span>
          {activeFilters.map((f) => (
            <span
              key={f.label}
              className="px-2 py-1 border border-brass text-brass bg-brass-soft/40 font-mono text-[10px] uppercase tracking-wider"
            >
              {f.label}: {f.value}
            </span>
          ))}
          <button
            onClick={clearFilters}
            className="ml-2 px-2 py-1 border border-ink-line text-ink-2 hover:border-crimson hover:text-crimson font-mono text-[10px] uppercase tracking-wider flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            Reset
          </button>
          <span className="ml-auto font-mono text-[11px] text-ink-2">
            <strong className="text-ink">{filtered.length}</strong> / {(data || []).length} BAP
          </span>
        </div>
      )}

      {/* UPLOAD CTA */}
      <div className="mb-6 flex items-center justify-between p-4 border border-ink-line bg-paper-2/30">
        <div>
          <h2 className="font-display font-bold text-[15px] flex items-center gap-2">
            <Upload className="w-4 h-4 text-brass" />
            Unggah BAP Baru
          </h2>
          <p className="font-mono text-[11px] text-ink-2 mt-1">
            Drop PDF/DOCX → ingest pipeline otomatis menjalankan OCR, chunk, indexing.
          </p>
        </div>
        <Link
          to="/berkas/upload"
          className="px-4 py-2 bg-ink text-paper hover:bg-brass transition font-mono text-[11px] uppercase tracking-wider font-semibold flex items-center gap-2"
        >
          <Upload className="w-3.5 h-3.5" />
          Buka Form Upload
        </Link>
      </div>

      {isLoading && <p className="font-mono text-sm text-ink-2">Memuat berkas...</p>}

      {data && data.length === 0 && (
        <div className="text-center py-16">
          <FileText className="w-12 h-12 mx-auto mb-3 text-ink-3" />
          <p className="font-display text-xl mb-2">Belum ada berkas</p>
          <p className="font-mono text-xs text-ink-3">Klik "Buka Form Upload" untuk mulai.</p>
        </div>
      )}

      {data && data.length > 0 && filtered.length === 0 && (
        <p className="font-mono text-xs text-ink-3 italic py-8 text-center">
          Tidak ada BAP sesuai filter. <button onClick={clearFilters} className="underline text-brass">Reset filter</button>
        </p>
      )}

      {filtered.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((doc) => {
            const klas = KLAS_STYLE[doc.classification] || KLAS_STYLE.BIASA;
            const processing = ['PENDING', 'PROCESSING'].includes(doc.status);
            return (
              <Link
                key={doc.id}
                to={`/berkas/${doc.id}`}
                className="group relative bg-paper-2/50 border border-ink-line p-4 flex flex-col gap-2 hover:bg-paper-2 hover:border-brass hover:-translate-y-px transition-all"
              >
                <span className="absolute -top-px left-4 w-15 h-1.5 bg-brass border-t border-brass-2" />
                <div className="font-mono text-[10px] tracking-widest text-ink-3 mt-2">
                  {doc.bap_number || doc.id.slice(0, 8)}
                </div>
                <div className="font-display font-semibold text-[15px] tracking-tight leading-tight">
                  {doc.case_id || 'Tanpa nomor LP'}
                </div>
                <div className="font-mono text-[10px] text-ink-2">
                  {doc.page_count || '—'} hal · {new Date(doc.uploaded_at).toLocaleDateString('id-ID')}
                </div>
                <div className="flex flex-wrap gap-1">
                  {doc.document_type && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 font-mono text-[9px] font-bold tracking-widest uppercase border border-brass text-brass">
                      {DOC_TYPE_LABEL[doc.document_type] || doc.document_type}
                    </span>
                  )}
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 font-mono text-[9px] font-bold tracking-widest uppercase border ${klas.color} ${klas.border} ${klas.bg}`}
                  >
                    <klas.Icon className="w-2.5 h-2.5" />
                    {klas.label}
                  </span>
                  {processing ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 font-mono text-[9px] font-semibold tracking-wider uppercase text-bluestamp border border-bluestamp/60">
                      <Loader2 className="w-2.5 h-2.5 animate-spin" />
                      {doc.status}
                    </span>
                  ) : doc.status === 'READY' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 font-mono text-[9px] font-semibold tracking-wider uppercase text-olive border border-olive/60">
                      <Sparkles className="w-2.5 h-2.5" />
                      AI Ready
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 font-mono text-[9px] font-semibold tracking-wider uppercase text-crimson border border-crimson/60">
                      {doc.status}
                    </span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
